'use client';

/**
 * useBottomBarInset — live height of the CommonNinja sticky bar (#82).
 *
 * The HSA/FSA promo bar is a THIRD-PARTY widget (CommonNinjaStickyBar in the
 * root layout): it lazy-loads 10–30s after page load, renders a
 * `position:fixed` bottom-anchored element inside the open shadowRoot of
 * `div.commonninja_component`, wraps taller on narrow viewports, and is
 * user-dismissible. The assistant page is an `h-dvh` surface, so without a
 * reservation the bar floats over the prompt input.
 *
 * Strategy: a cheap interval poll (one shadow query + one rect read), the
 * same shape as `waitForHubSpotSDK` in src/lib/handoff.ts — the repo's
 * canonical "wait on a lazy third-party global" pattern. Polling (vs
 * MutationObserver/ResizeObserver plumbing) is deliberate: the widget owns
 * its shadow tree and re-renders it freely, the bar can appear/disappear/
 * resize at any time, and a 750ms cadence is imperceptible for layout
 * padding while surviving every widget behavior change short of a class
 * rename — and if `.notification-bar` IS renamed, we degrade to inset 0,
 * i.e. exactly today's behavior, never a broken layout.
 *
 * Returns the current inset in px (0 = no bar). Consumed by AssistantLayout
 * as bottom padding so the whole card (input bar included) lifts above the
 * promo bar and reclaims the space the moment the bar is dismissed.
 */

import { useEffect, useState } from 'react';

const POLL_MS = 750;
/** rect.bottom may be a fraction of a px off the viewport edge. */
const BOTTOM_ANCHOR_TOLERANCE_PX = 4;

/**
 * Pure inset rule — exported for unit tests.
 *
 * Reserve space only for a bar that is actually occluding the viewport
 * bottom: fixed-position, rendered (non-zero height, not display:none /
 * visibility:hidden), and anchored to the bottom edge. Anything else
 * (dismissed, mid-animation off-screen, CommonNinja placeholder) → 0.
 */
export function computeBarInset(args: {
  height: number;
  bottom: number;
  position: string;
  display: string;
  visibility: string;
  viewportHeight: number;
}): number {
  const { height, bottom, position, display, visibility, viewportHeight } =
    args;
  if (position !== 'fixed') return 0;
  if (height <= 0) return 0;
  if (display === 'none' || visibility === 'hidden') return 0;
  if (Math.abs(bottom - viewportHeight) > BOTTOM_ANCHOR_TOLERANCE_PX) return 0;
  return Math.ceil(height);
}

/** Locate + measure the live bar. 0 when absent/dismissed/not yet loaded. */
function measureBar(): number {
  const host = document.querySelector('.commonninja_component');
  const bar = host?.shadowRoot?.querySelector('.notification-bar');
  if (!(bar instanceof HTMLElement)) return 0;
  const rect = bar.getBoundingClientRect();
  const cs = getComputedStyle(bar);
  return computeBarInset({
    height: rect.height,
    bottom: rect.bottom,
    position: cs.position,
    display: cs.display,
    visibility: cs.visibility,
    viewportHeight: window.innerHeight,
  });
}

export function useBottomBarInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    // setState with an unchanged number bails out of re-render, so the
    // steady-state cost of the poll is one DOM read per tick — no renders.
    const tick = () => setInset(measureBar());
    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  return inset;
}
